import type { BuildingType, UnitType } from "../shared/game-types.js";
import type { ClientDiagnostic, ClientMessage, ServerMessage } from "../shared/protocol.js";

export type ConnectionCallbacks = {
  onReady: () => void;
  onStatus: (status: "connecting" | "connected" | "disconnected") => void;
  onMessage: (message: ServerMessage) => void;
};

const CLIENT_ID_KEY = "alvasAttackTabClientId";
const STALE_CONNECTION_MS = 6_000;

function getClientId(): string {
  const existing = sessionStorage.getItem(CLIENT_ID_KEY);
  if (existing) {
    return existing;
  }
  const created = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : "client-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
  sessionStorage.setItem(CLIENT_ID_KEY, created);
  return created;
}

export class GameConnection {
  private readonly callbacks: ConnectionCallbacks;
  private readonly clientId = getClientId();
  private socket: WebSocket | null = null;
  private playerName: string;
  private ready = false;
  private reconnectTimer: number | null = null;
  private staleConnectionTimer: number | null = null;
  private requestedRoomCode: string | null = null;
  private joinedRoomCode: string | null = null;
  private closedIntentionally = false;
  private diagnosticsEnabled = false;
  private connectionGeneration = 0;
  private reconnectCount = 0;
  private messagesReceived = 0;

  constructor(playerName: string, callbacks: ConnectionCallbacks) {
    this.playerName = playerName;
    this.callbacks = callbacks;
  }

  connect(): void {
    this.closedIntentionally = false;
    this.clearReconnectTimer();
    this.clearStaleConnectionTimer();
    this.connectionGeneration += 1;
    if (this.connectionGeneration > 1) {
      this.reconnectCount += 1;
    }
    this.callbacks.onStatus("connecting");
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(protocol + "//" + window.location.host);
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.socket !== socket) {
        socket.close();
        return;
      }
      this.callbacks.onStatus("connected");
      this.send({ type: "hello", clientId: this.clientId, name: this.playerName });
    });
    socket.addEventListener("message", (event) => {
      if (this.socket !== socket) {
        return;
      }
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch (_error) {
        return;
      }
      this.messagesReceived += 1;
      if (message.type === "hello_ok") {
        const becameReady = !this.ready;
        this.ready = true;
        this.diagnosticsEnabled = Boolean(message.diagnosticsEnabled);
        if (becameReady) {
          const roomRequestedBeforeReady = this.requestedRoomCode;
          this.callbacks.onReady();
          if (roomRequestedBeforeReady) {
            this.send({ type: "join_room", code: roomRequestedBeforeReady });
          }
        }
      }
      if (message.type === "room_joined") {
        this.requestedRoomCode = message.code;
        this.joinedRoomCode = message.code;
        this.armStaleConnectionTimer();
      }
      if (message.type === "snapshot" && this.joinedRoomCode) {
        this.armStaleConnectionTimer();
      }
      this.callbacks.onMessage(message);
    });
    socket.addEventListener("close", () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = null;
      this.ready = false;
      this.clearStaleConnectionTimer();
      if (this.closedIntentionally) {
        return;
      }
      this.callbacks.onStatus("disconnected");
      this.reconnectTimer = window.setTimeout(() => this.connect(), 1_000);
    });
    socket.addEventListener("error", function () {
      // The close handler drives reconnect and visible status.
    });
  }

  setPlayerName(name: string): void {
    this.playerName = name.trim().slice(0, 24) || "Skogsvän";
    if (this.ready) {
      this.send({ type: "hello", clientId: this.clientId, name: this.playerName });
    }
  }

  createRoom(): void {
    this.requestedRoomCode = null;
    this.joinedRoomCode = null;
    this.send({ type: "create_room" });
  }

  createAiRoom(): void {
    this.requestedRoomCode = null;
    this.joinedRoomCode = null;
    this.send({ type: "create_ai_room" });
  }

  joinRoom(code: string): void {
    this.requestedRoomCode = code.trim().toUpperCase();
    if (this.ready) {
      this.send({ type: "join_room", code: this.requestedRoomCode });
    }
  }

  placeBuilding(lane: number, column: number, buildingType: BuildingType): void {
    this.send({ type: "place_building", lane, column, buildingType });
  }

  claimGold(buildingId: string): void {
    this.send({ type: "claim_gold", buildingId });
  }

  buyUnit(buildingId: string, unitType: UnitType): void {
    this.send({ type: "buy_unit", buildingId, unitType });
  }

  setDenQueueMode(buildingId: string, enabled: boolean): void {
    this.send({ type: "set_den_queue_mode", buildingId, enabled });
  }

  reorderUnitQueue(buildingId: string, fromIndex: number, toIndex: number): void {
    this.send({ type: "reorder_unit_queue", buildingId, fromIndex, toIndex });
  }

  removeQueuedUnit(buildingId: string, index: number): void {
    this.send({ type: "remove_queued_unit", buildingId, index });
  }

  releaseNextQueuedUnit(buildingId: string): void {
    this.send({ type: "release_next_queued_unit", buildingId });
  }

  releaseUnitQueue(buildingId: string): void {
    this.send({ type: "release_unit_queue", buildingId });
  }

  requestRestart(): void {
    this.send({ type: "request_restart" });
  }

  diagnosticsAreEnabled(): boolean {
    return this.diagnosticsEnabled;
  }

  reportDiagnostic(
    sample: Omit<ClientDiagnostic, "connectionGeneration" | "reconnectCount" | "messagesReceived" | "socketReadyState">,
  ): void {
    if (!this.diagnosticsEnabled || !this.ready) {
      return;
    }
    this.send({
      type: "client_diagnostic",
      sample: {
        ...sample,
        connectionGeneration: this.connectionGeneration,
        reconnectCount: this.reconnectCount,
        messagesReceived: this.messagesReceived,
        socketReadyState: this.socket?.readyState ?? WebSocket.CLOSED,
      },
    });
  }

  close(): void {
    this.closedIntentionally = true;
    this.diagnosticsEnabled = false;
    this.clearReconnectTimer();
    this.clearStaleConnectionTimer();
    const socket = this.socket;
    this.socket = null;
    socket?.close();
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private armStaleConnectionTimer(): void {
    this.clearStaleConnectionTimer();
    this.staleConnectionTimer = window.setTimeout(() => {
      this.staleConnectionTimer = null;
      if (!this.closedIntentionally && this.socket) {
        const staleSocket = this.socket;
        this.socket = null;
        this.ready = false;
        staleSocket.close(4_000, "Snapshot stream stalled");
        this.connect();
      }
    }, STALE_CONNECTION_MS);
  }

  private clearStaleConnectionTimer(): void {
    if (this.staleConnectionTimer !== null) {
      window.clearTimeout(this.staleConnectionTimer);
      this.staleConnectionTimer = null;
    }
  }
}
